import { Injectable, inject, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription, timer } from 'rxjs';
import { VersionUpdateModalComponent } from '../components/version-update-modal/version-update-modal.component';

@Injectable({ providedIn: 'root' })
export class VersionUpdateService implements OnDestroy {
  private readonly updates = inject(SwUpdate);
  private readonly bsModalService = inject(BsModalService);

  private bsModalRef: BsModalRef | null = null;
  private readonly CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes
  private intervalSubscription?: Subscription;
  private versionUpdateSubscription?: Subscription;
  private visibilityChangeHandler?: () => void;

  constructor() {
    // Check on app startup
    this.checkForUpdate();

    this.registerEvents();
    this.startCheckInterval();
  }

  public reloadPageAndRefreshCache(): void {
    void this.clearServiceWorkerCache();
    window.location.href = window.location.origin + '?nocache=' + new Date().getTime().toString();
  }

  private async clearServiceWorkerCache(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(async (reg) => {
        await reg.update();
      }));
    } catch (error: unknown) {
      console.error(error);
    }
  }

  private startCheckInterval(): void {
    // check periodically for updates
    this.intervalSubscription = timer(this.CHECK_INTERVAL, this.CHECK_INTERVAL).subscribe(() => {
      this.checkForUpdate();
    });
  }

  private registerEvents(): void {
    this.versionUpdateSubscription = this.updates.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.showVersionUpdateModal();
      }
    });

    // check when app gains focus
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  private showVersionUpdateModal(): void {
    this.bsModalRef = this.bsModalService.show(VersionUpdateModalComponent, {
      ignoreBackdropClick: true
    });
  }

  private checkForUpdate(): void {
    if (!this.isServiceWorkerActive()) return;
    this.updates.checkForUpdate().catch((error: unknown) => {
      console.warn(error);
    });
  }

  private isServiceWorkerActive(): boolean {
    if (!('serviceWorker' in navigator)) return false;
    return this.updates.isEnabled;
  }

  public ngOnDestroy(): void {
    this.intervalSubscription?.unsubscribe();
    this.versionUpdateSubscription?.unsubscribe();
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }
}
